import React from "react";
import { SubscriptionSummary } from "../models/Subscription";
import { Grid, ListItemText, Typography, makeStyles } from "@material-ui/core";
import { Link } from "react-router-dom";
import { DateDisplay } from "./DateDisplay";
import { ValueDisplay } from "./ValueDisplay";
import { toSubscriptionDetails } from "../router/navigation";
import colors from "../constants/colors";

interface SubscriptionDisplayCardProps {
	//Id of product.
	productId: string;
	//Summary for subscription to display
	subscription: SubscriptionSummary;
	//THis is a temporary variable needed for swapping the link elements between a react router link (the default) and an html style link.
	htmlLink?: boolean;
}
const useStyles = makeStyles((theme) => ({
	listSecondary: {
		marginTop: theme.spacing(0.5),
		fontSize: theme.typography.caption.fontSize,
	},
	details: {
		fontSize: theme.typography.body2.fontSize,
	},
	detailsItem: {
		marginTop: theme.spacing(1),
	},
	expiredBadge: {
		fontSize: 11,
		color: colors.gray700,
		backgroundColor: colors.gray300,
		padding: "4px 8px",
		borderRadius: 4,
		fontWeight: 700,
		letterSpacing: ".1em",
		marginLeft: "12px",
	},
}));

/**
 * Display card for a subscription.
 */
export const SubscriptionDisplayCard = (props: SubscriptionDisplayCardProps) => {
	const { subscription, productId } = props;
	const classes = useStyles();
	return (
		<Grid container>
			<Grid item xs={12}>
				<ListItemText
					data-testid="subscription-customer-description"
					classes={{ secondary: classes.listSecondary }}
					primary={
						<Grid container justify="space-between">
							<Grid item data-testid="subscription-company-name">
								{subscription.companyName}

								{!subscription.isValidLicense && <span className={classes.expiredBadge}>Expired</span>}
							</Grid>
							<Grid item>
								<Typography variant="caption" data-testid="subscription-created-date">
									created on <DateDisplay date={subscription.createdDate} />
								</Typography>
							</Grid>
						</Grid>
					}
					secondary={<React.Fragment>{subscription.customerKeyDescription}</React.Fragment>}
				/>
			</Grid>
			<Grid container className={classes.details}>
				<Grid item xs={12} sm={6} md={4} className={classes.detailsItem} data-testid="subscription-id">
					<ValueDisplay
						label="Subscription ID"
						node={
							props.htmlLink ? (
								<a href={toSubscriptionDetails(productId, subscription.subscriptionId)}>
									{subscription.subscriptionId}
								</a>
							) : (
								<Link variant="body2" to={toSubscriptionDetails(productId, subscription.subscriptionId)}>
									{subscription.subscriptionId}
								</Link>
							)
						}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={4} className={classes.detailsItem} data-testid="subscription-product-tier">
					<ValueDisplay label="Product tier" node={subscription.productTierName} />
				</Grid>
				<Grid item xs={12} sm={6} md={4} className={classes.detailsItem} data-testid="subscription-product-license-key">
					<ValueDisplay label="Product license key" node={subscription.productLicenseKey} />
				</Grid>
			</Grid>
		</Grid>
	);
};
